"use client";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../slice/hooks";
import StoreBox from "./store-box";
import CartModal from "./cart-modal";
import Progress from "./progress";
import { getActiveUser } from "../slice/auth-slice";
import {
  getLoggedinUser,
  useFetchGuestCollections,
  useFetchUserCollections,
} from "../../lib/util";
import { useToken } from "../../lib/context/auth-context";

import Skeleton from "react-loading-skeleton";
import { useSwitchingOrgs } from "../organisation/useSwitchingOrgs";
import { cartIcon } from "../../assets";

import { useHistoryManager } from "../history/useHistoryManager";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import { setActiveCategory, setCategories } from "../slice/category-slice";
import { useQueryClient } from "@tanstack/react-query";

const MarketplaceSkeleton = () => {
  return (
    <div className="cart-container">
      <div className="cart-div">
        {Array.from({ length: 3 }).map((_, storeIdx) => (
          <div
            key={storeIdx}
            style={{
              padding: "10px",
              paddingTop: 0,
              marginBottom: "24px",
              borderRadius: "12px",
              background: "#fff",
            }}
          >
            {/* Store header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              {/* Store logo */}
              <Skeleton width={48} height={48} borderRadius={8} />

              {/* Store name */}
              <Skeleton width={180} height={22} />
            </div>

            {/* Cart container */}
            <div
              style={{
                borderRadius: "12px",
                padding: "16px",
                background: "#fafafa",
                display: "flex",
                flexDirection: "column",
                gap: "2rem",
              }}
            >
              {/* Cart rows */}
              {Array.from({ length: 3 }).map((_, itemIdx) => (
                <div
                  key={itemIdx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  {/* Product thumbnail */}
                  <Skeleton width={56} height={56} borderRadius={8} />

                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    {/* Product name */}
                    <Skeleton width="40%" height={14} />

                    {/* Price / meta */}
                    <Skeleton width="70%" height={16} />
                  </div>

                  {/* Quantity / action */}
                  <Skeleton width={40} height={28} borderRadius={6} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HostHeader = ({ host }) => {
  const [logoLoaded, setLogoLoaded] = useState(false);

  const hostImage = host?.image;
  const hostName = host?.name;

  const logoSize = 48;

  return (
    <div
      style={{
        display: "flex",
        marginBottom: "2rem",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        aria-hidden
        style={{
          width: logoSize,
          height: logoSize,
          borderRadius: 8,
          overflow: "hidden",
          flexShrink: 0,
          background: "#f6f6f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {hostImage ? (
          <>
            {!logoLoaded && <Skeleton width={logoSize} height={logoSize} />}
            <img
              src={hostImage}
              alt={hostName ? `${hostName} logo` : "Organisation logo"}
              onLoad={() => setLogoLoaded(true)}
              onError={() => setLogoLoaded(true)}
              style={{
                width: logoSize,
                height: logoSize,
                objectFit: "cover",
                display: logoLoaded ? "block" : "none",
              }}
              width={logoSize}
              height={logoSize}
            />
          </>
        ) : null}
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        {hostName ? (
          <span
            style={{
              fontSize: "2.5rem",
              fontWeight: 500,
              lineHeight: 1.1,
            }}
            aria-live="polite"
          >
            {hostName}
          </span>
        ) : null}
        <span style={{ fontSize: "0.9rem", color: "#666" }} />
      </div>
    </div>
  );
};

const MarketplaceCartPage = (props) => {
  const {
    closeModal,
    showProduct,
    activateBackButton,
    storePath,
    modal,
    showProgress,
    setShowProgress,
    navLocation,
    urlData,
  } = props;

  const dispatch = useAppDispatch();
  const [isPageMode, setIsPageMode] = useState(modal ? false : true);
  const [publicUser, setPublicUser] = useState(null);

  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser())
  );
  const organisation = useAppSelector((state) => state.accessOrg.organisation);
  const accessPoint = useAppSelector((state) => state.accessOrg.accessPoint);
  const accessPoints = useAppSelector((state) => state.accessOrg.accessPoints);

  const [cartCollections, setCartCollections] = useState([]);
  const { token } = useToken();
  const userId = loggedInUser?.user?.userId ?? null;
  const loggedToken = loggedInUser?.token ?? null;
  const queryClient = useQueryClient();

  const [checkoutStates, setCheckoutStates] = useState({});
  const [activeProgressCollectionId, setActiveProgressCollectionId] =
    useState(null);

  const [isSwitching, setIsSwitching] = useState(false);

  const {
    data: userCollectionsRes,
    isLoading: isLoadingUserCollections,
    isFetching: isFetchingUserCollections,
    refetch: refetchUserCollections,
  } = useFetchUserCollections({
    userId,
    token: loggedToken,
    enabled: !!userId,
    onError: (err) => console.error(err?.message || err),
  });

  const {
    data: guestCollectionsRes,
    isLoading: isLoadingGuestCollections,
    isFetching: isFetchingGuestCollections,
    refetch: refetchGuestCollections,
  } = useFetchGuestCollections({
    token,
    enabled: !userId,
    onError: (err) => console.error(err?.message || err),
  });

  const isFetchingCollections =
    isFetchingUserCollections || isFetchingGuestCollections;

  // sync local state from query data (use cached data on remount)
  useEffect(() => {
    const userCollections = userCollectionsRes?.data?.collections ?? null;
    const guestCollections = guestCollectionsRes?.data?.collections ?? null;
    const collections = userId ? userCollections : guestCollections ?? [];

    // simple stable-id based shallow-equality check
    const same =
      Array.isArray(collections) &&
      Array.isArray(cartCollections) &&
      collections.length === cartCollections.length &&
      collections.every((c, i) => {
        const current = cartCollections[i];

        // Check collection ID
        if (
          (c.collectionId ?? c.id ?? "") !==
          (current?.collectionId ?? current?.id ?? "")
        ) {
          return false;
        }

        // Check number of items in paymentItem
        const newItems = c.paymentItem ?? [];
        const oldItems = current?.paymentItem ?? [];
        if (newItems.length !== oldItems.length) {
          return false;
        }

        return true;
      });

    if (!same) {
      setCartCollections(Array.isArray(collections) ? collections : []);
    }
  }, [userCollectionsRes, guestCollectionsRes, userId]);

  // Keep only collections that have paymentItem (non-empty)
  const visibleCollections = useMemo(
    () =>
      (cartCollections || []).filter(
        (c) => (c?.paymentItem ?? []).length !== 0
      ),
    [cartCollections]
  );

  // Group by host.profileId
  const groupedByHost = useMemo(() => {
    const map = new Map();

    for (const collection of visibleCollections) {
      const host = collection?.host ?? {};
      const hostId = host.profileId ?? host.orgId ?? "unknown-host";

      if (!map.has(hostId)) {
        map.set(hostId, { host, hostId, collections: [] });
      }
      map.get(hostId).collections.push(collection);
    }

    return Array.from(map.values());
  }, [visibleCollections]);

  const {
    startSwitch,
    isSwitching: hookSwitching,
    error: switchError,
  } = useSwitchingOrgs({ token });

  const { navigateTo } = useHistoryManager(storePath);

  // Stable callbacks
  const handleActivateBackButton = useCallback(
    (show, onClickBackButton = null) => {
      activateBackButton?.(show, onClickBackButton);
      setShowProgress(show);
    },
    [activateBackButton, setShowProgress]
  );

  // get checkout state for specific collection (primitive, default 'cartItems')
  const getCheckoutState = useCallback(
    (collectionId) => {
      return checkoutStates[collectionId] ?? "cartItems";
    },
    [checkoutStates]
  );

  // set checkout state for a collection (store primitive value)
  const setCheckoutStateFor = useCallback((collectionId, newState) => {
    if (!collectionId) return;
    setCheckoutStates((prev) => {
      // if same value, return prev to avoid rerenders
      if (prev[collectionId] === newState) return prev;
      return {
        ...prev,
        [collectionId]: newState,
      };
    });
  }, []);

  useEffect(() => {
    setIsSwitching(hookSwitching);
  }, [hookSwitching]);

  useEffect(() => {
    dispatch(setAccessPointId(null));
    dispatch(setAccessPoint(null));
    dispatch(setAccessPoints([]));
    dispatch(setOrganisation(null));
    dispatch(setActiveCategory(null));
    dispatch(setCategories(null));
  }, []);

  useEffect(() => {
    try {
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: ["userCollections", userId, loggedToken],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["guestCollections", token],
        });
      }
    } catch (e) {
      console.log("invalidateCollections error", e);
    }
  }, [userId, loggedToken, token, queryClient]);

  const handleLocationChange = useCallback(() => {}, []);
  // const showSwitchingSkeleton = isSwitching || isFetchingCollections;
  const showSwitchingSkeleton =
    isSwitching || (isFetchingCollections && cartCollections.length === 0);

  return (
    <StoreBox
      {...props}
      handleLocationChange={handleLocationChange}
      isPageMode={isPageMode}
      isSwitching={isSwitching}
      organisation={organisation}
      accessPoint={accessPoint}
      accessPoints={accessPoints}
    >
      <div className="cart-container">
        <div className="cart-div">
          <div className="marketplaceCartHeading">
            <p>Checkout your goods from our stores.</p>
          </div>
          <div>
            {showSwitchingSkeleton ? (
              <MarketplaceSkeleton />
            ) : groupedByHost.length > 0 ? (
              groupedByHost.map((group) => {
                const groupKey = group.hostId;
                return (
                  <div key={groupKey} style={{ padding: "20px" }}>
                    <HostHeader host={group.host} />
                    {group.collections.map((collection, idx) => {
                      const collectionId = collection?.collectionId;
                      const itemsLength = collection?.paymentItem?.length ?? 0;
                      const key = `${collectionId}-${itemsLength}`;
                      return (
                        <div key={key}>
                          <div className="auth-form-header ">
                            {showProgress &&
                              activeProgressCollectionId === collectionId && (
                                <div className="auth-form-header">
                                  <Progress
                                    checkoutState={getCheckoutState(
                                      collectionId
                                    )}
                                    setCheckoutState={(st) =>
                                      setCheckoutStateFor(collectionId, st)
                                    }
                                    accessPoint={accessPoint}
                                    publicUser={publicUser}
                                  />
                                </div>
                              )}
                          </div>

                          <CartModal
                            cart={collection}
                            showProduct={showProduct}
                            closeModal={closeModal}
                            accessPoint={accessPoint}
                            accessPoints={accessPoints}
                            activateBackButton={handleActivateBackButton}
                            checkoutState={getCheckoutState(collectionId)}
                            setCheckoutState={(st) =>
                              setCheckoutStateFor(collectionId, st)
                            }
                            publicUser={publicUser}
                            setPublicUser={setPublicUser}
                            isPageMode={isPageMode}
                            storePath={storePath}
                            navLocation={navLocation}
                            userId={userId}
                            userToken={loggedToken}
                            token={token}
                            activeProgressCollectionId={
                              activeProgressCollectionId
                            }
                            setActiveProgressCollectionId={
                              setActiveProgressCollectionId
                            }
                            startSwitch={startSwitch}
                            organisation={organisation}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })
            ) : (
              // Fallback: not loading and no grouped collections
              <div className="empty-cart-message">
                <span>{cartIcon}</span>
                <h3>Your cart is empty.</h3>
                <p>
                  Looks like you haven&apos;t added anything to your cart yet.
                </p>
                <button
                  className="checkout-secondary-button"
                  onClick={() => navigateTo("home", {}, false)}
                >
                  Continue shopping
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </StoreBox>
  );
};

export default MarketplaceCartPage;
